<template>
    <main
        :class="[
            templateVersionClone.active === true ? 'h-[300px]' : 'h-[48px]',
            'relative mt-5 rounded-md border border-gray-300',
        ]"
    >
        <div class="flex h-[48px] items-center justify-between px-2 shadow-sm select-none">
            <SwitchButton
                v-model="templateVersionClone.active"
                :state="templateVersionClone.active ? 'success' : 'neutral'"
                :label="t('buttonImportFromAnother')"
            />
            <div v-if="templateVersionClone.active === true && selectedTemplateId !== '0'">
                <button
                    type="button"
                    class="focus:ring-none cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                    @click="resetDatasets()"
                >
                    <span class="sr-only">Close</span>
                    <MdiArrowLeft
                        class="h-6 w-6"
                        aria-hidden="true"
                    />
                </button>
            </div>
        </div>
        <TransitionRoot
            :show="templateVersionClone.active === true && selectedTemplateId === '0'"
            as="div"
            class="absolute top-12 right-0 bottom-0 left-0 z-[1]"
        >
            <TransitionChild
                as="div"
                enter="transform transition ease-in-out duration-300"
                enter-from="scale-0 opacity-0"
                enter-to="scale-100 opacity-100"
                leave="transform transition ease-in-out duration-300"
                leave-from="scale-100 opacity-100"
                leave-to="scale-0 opacity-0"
            >
                <div
                    v-if="templateVersionClone.active === true && selectedTemplateId === '0'"
                    class="h-[250px] max-h-[250px] min-h-[250px] divide-y divide-gray-300 overflow-y-auto py-3 select-none"
                >
                    <div
                        v-for="tpl in templates.data.items"
                        :key="tpl.response.code"
                        class="group/template hover:bg-gray-100"
                    >
                        <div
                            class="flex cursor-pointer gap-2 px-2 py-1"
                            @click="activateTemplate(tpl.response.templateId)"
                        >
                            <div class="flex-none px-2 font-medium text-gray-600">
                                <MdiFilePdfBox
                                    v-if="tpl.response.type == 'PDF'"
                                    class="mr-1.5 h-7 w-7 shrink-0 text-rose-400"
                                />
                                <MdiEmail
                                    v-if="tpl.response.type == 'EMAIL'"
                                    class="mr-1.5 h-7 w-7 shrink-0 text-sky-400"
                                />
                                <MdiCellphoneText
                                    v-if="tpl.response.type == 'SMS'"
                                    class="mr-1.5 h-7 w-7 shrink-0 text-green-400"
                                />
                                <MdiInbox
                                    v-if="tpl.response.type == 'INBOX'"
                                    class="mr-1.5 h-7 w-7 shrink-0 text-amber-400"
                                />
                            </div>
                            <div class="grow flex-col gap-2">
                                <div class="text-sm font-medium text-gray-900">
                                    {{ tpl.response.name }}
                                </div>
                                <div class="text-xs font-normal text-gray-500">
                                    {{ tpl.response.code }}
                                </div>
                            </div>
                            <div
                                class="justify-end px-2 font-medium text-white group-hover/template:text-gray-500"
                            >
                                <MdiArrowRight class="inline h-5 w-5" />
                            </div>
                        </div>
                    </div>
                </div>
            </TransitionChild>
        </TransitionRoot>

        <TransitionRoot
            :show="selectedTemplateId !== '0'"
            as="div"
            class="absolute top-12 right-0 bottom-0 left-0"
        >
            <TransitionChild
                as="div"
                enter="transform transition ease-in-out duration-300"
                enter-from="scale-0 opacity-0"
                enter-to="scale-100 opacity-100"
                leave="transform transition ease-in-out duration-300"
                leave-from="scale-100 opacity-100"
                leave-to="scale-0 opacity-0"
            >
                <div
                    v-if="selectedTemplateId !== '0'"
                    class="h-[250px] max-h-[250px] min-h-[250px] divide-y divide-gray-300 overflow-y-auto py-3 select-none"
                >
                    <div
                        v-for="templateVersion in templateVersions.data.items"
                        :key="templateVersion.templateVersionId"
                        class="group/template-version hover:bg-gray-100"
                        :class="[
                            Object.keys(templateVersionClone.data).length > 1 &&
                            templateVersionClone.data.templateVersionId ===
                                templateVersion.templateVersionId
                                ? 'bg-green-100 hover:bg-green-100'
                                : 'hover:bg-gray-100',
                            'group/template-version',
                        ]"
                    >
                        <div
                            class="flex cursor-pointer gap-2 px-2 py-1"
                            @click="activateTemplateVersion(templateVersion.templateVersionId)"
                        >
                            <div class="grow flex-col gap-2">
                                <div class="text-base font-medium text-gray-900">
                                    {{ templateVersion.changeLog }}
                                </div>
                                <div class="text-xs font-normal text-gray-500">
                                    {{ t('templateVersion') }}:&nbsp;<strong>{{
                                        templateVersion.version
                                    }}</strong>
                                </div>
                            </div>
                            <div
                                :class="[
                                    Object.keys(templateVersionClone.data).length > 1 &&
                                    templateVersionClone.data.templateVersionId ===
                                        templateVersion.templateVersionId
                                        ? 'text-green-500 group-hover/template-version:text-green-500'
                                        : 'text-white group-hover/template-version:text-gray-500',
                                    'justify-end px-2 font-medium',
                                ]"
                            >
                                <MdiCheck class="inline h-5 w-5" />
                            </div>
                        </div>
                    </div>
                </div>
            </TransitionChild>
        </TransitionRoot>
    </main>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { t } from '../composables/i18n';
import { getTemplates, getTemplateVersion, getTemplateVersions } from '../composables/useTemplates';
import {
    MdiFilePdfBox,
    MdiEmail,
    MdiCellphoneText,
    MdiInbox,
    MdiArrowLeft,
    MdiCheck,
    MdiArrowRight,
} from 'materialdesignicons-vue3/icons/';
import { TransitionRoot, TransitionChild } from '@headlessui/vue';
import SwitchButton from '../ui/SwitchButton.vue';

defineExpose({
    name: 'ImportTemplateVersion',
});

const props = defineProps({
    templateVersionClone: {
        type: Object,
        required: true,
    },
    template: {
        type: Object,
        required: true,
    },
});

const templateVersionClone = props.templateVersionClone;
const template = props.template;
const selectedTemplateId = ref('0');
const templates = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
});
const templateVersions = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
});

const activateTemplate = async (templateId) => {
    templateVersions.data = await getTemplateVersions({ templateId: templateId });
    selectedTemplateId.value = templateId;
};

const activateTemplateVersion = async (templateVersionId) => {
    templateVersionClone.data = await getTemplateVersion(templateVersionId);
};

const resetDatasets = async () => {
    templateVersions.data = {
        items: [],
        totalCount: 0,
    };
    selectedTemplateId.value = '0';
    templateVersionClone.data = {};
};

const refreshTemplates = async () => {
    if (templateVersionClone.active === true) {
        templates.data = await getTemplates({
            type: template.type,
        });
    }
};

watch(
    () => template.type,
    async () => {
        await resetDatasets();
        await refreshTemplates();
    },
);

watch(
    () => templateVersionClone.active,
    async (newVal) => {
        if (newVal === false) {
            await resetDatasets();
        }
        await refreshTemplates();
    },
);
</script>
